import { useState } from 'react';
import type { BedConfig, BedSpace, NewRoom, RoomType, UnitShape } from '@lemontaigu/pms-core';
import { SpaceEditor, UnitScalars } from './UnitFields.js';

// « Nouvelle chambre » (room-edit brief E5) — identity plus the few fields that
// make the row render sensibly. Everything else is filled afterwards in the
// fiche, so a new room is never half-described by defaults nobody chose.
//
// This split is a DESIGN CHOICE about where a field is best captured, not a
// rule. It was previously justified here as "keeps this form small (Hard Rule
// 4)" — a misattribution the operator caught on 2026-08-11: Hard Rule 4 governs
// phasing (nothing built before its phase; each phase designed before
// implementation), and says nothing about how many fields a screen carries.
// Once the design is known, "the form is small" needs its own argument.
//
// There is no renumbering anywhere: the number is the identity every
// reservation, block and event points at. It is set once, here.

export interface RoomCreateProps {
  /** True when the établissement in scope lets APARTMENTS rather than rooms
   *  (`app_property.kind`). It changes the whole form, not a label: an
   *  apartment brings its own category (M10 — 1 type ↔ 1 unit ↔ 1 Channex room
   *  type), describes its composition, and lists beds space by space. */
  apartments: boolean;
  roomTypes: RoomType[];
  bedConfigs: BedConfig[];
  saving: boolean;
  error: string;
  onCreate: (draft: NewRoom) => void;
  onClose: () => void;
}

/** 114 → 1. A convention, not a rule — hence prefilled and overridable rather
 *  than derived silently. */
const floorFromNumber = (n: string): number | null => {
  const first = Number(n.trim()[0]);
  return Number.isFinite(first) && first > 0 ? first : null;
};

const EMPTY_UNIT: UnitShape = {
  unitKind: 'apartment',
  bedrooms: null,
  livingRooms: null,
  bathrooms: null,
  wcCount: null,
  kitchen: null,
  surfaceSqm: null,
};

export function RoomCreate({
  apartments,
  roomTypes,
  bedConfigs,
  saving,
  error,
  onCreate,
  onClose,
}: RoomCreateProps) {
  const [roomNumber, setRoomNumber] = useState('');
  const [floor, setFloor] = useState<number | null>(null);
  const [floorTouched, setFloorTouched] = useState(false);
  const [roomTypeId, setRoomTypeId] = useState<number | null>(null);
  const [bedConfig, setBedConfig] = useState<string | null>(null);
  const [maxOccupancy, setMaxOccupancy] = useState(2);
  // Apartment-only. A new unit brings its own category, so the operator names
  // it here rather than picking one that cannot exist yet.
  const [typeCode, setTypeCode] = useState('');
  const [typeLabel, setTypeLabel] = useState('');
  const [unit, setUnit] = useState<UnitShape>(EMPTY_UNIT);
  const [spaces, setSpaces] = useState<BedSpace[]>([]);

  const effectiveFloor = floorTouched ? floor : floorFromNumber(roomNumber);

  // What is still missing, NAMED — not just a boolean. A disabled button with no
  // stated reason is indistinguishable from a broken one: the operator filled
  // every visible field, left « Nom du logement » on its placeholder (which
  // reads as a value because the placeholder IS the value they wanted), and
  // reported « je ne peux pas le créer » (2026-08-11). The button now says why.
  //
  // An apartment must name its category and describe at least one space — the
  // second is the "never neither" half of the XOR, met here as form validation
  // rather than as a database error after the fact.
  const missing: string[] = [
    ...(roomNumber.trim() === '' ? [apartments ? 'le nom du logement' : 'le n° de chambre'] : []),
    ...(effectiveFloor === null ? ["l'étage"] : []),
    ...(maxOccupancy >= 1 ? [] : ['une capacité d’au moins 1']),
    ...(apartments && typeCode.trim() === '' ? ['le code catégorie'] : []),
    ...(apartments && spaces.length === 0 ? ['au moins une pièce'] : []),
  ];
  const valid = missing.length === 0;

  return (
    <aside className="pms-sheet" aria-label={apartments ? 'Nouvel appartement' : 'Nouvelle chambre'}>
      <header className="pms-sheet-head">
        <h3>{apartments ? 'Nouvel appartement' : 'Nouvelle chambre'}</h3>
        <button type="button" className="pms-sheet-close" onClick={onClose} aria-label="Fermer">
          ✕
        </button>
      </header>

      {error && <p className="auth-err">Erreur : {error}</p>}

      <div className="pms-sheet-body">
        <label>
          {apartments ? 'Nom du logement' : 'N° de chambre'}
          <input
            type="text"
            value={roomNumber}
            placeholder={apartments ? 'Apt0' : '415'}
            autoFocus
            onChange={(e) => setRoomNumber(e.target.value)}
          />
        </label>

        <label>
          Étage
          <input
            type="number"
            min={0}
            max={9}
            value={effectiveFloor ?? ''}
            onChange={(e) => {
              setFloorTouched(true);
              setFloor(e.target.value === '' ? null : Number(e.target.value));
            }}
          />
        </label>

        {apartments ? (
          <>
            <label>
              Code catégorie
              <input
                type="text"
                value={typeCode}
                placeholder="APT0"
                onChange={(e) => setTypeCode(e.target.value.toUpperCase())}
              />
            </label>
            <label>
              Libellé catégorie
              <input
                type="text"
                value={typeLabel}
                placeholder="Appartement 0 — rez-de-chaussée"
                onChange={(e) => setTypeLabel(e.target.value)}
              />
            </label>
          </>
        ) : (
          <label>
            Catégorie
            <select
              value={roomTypeId === null ? '' : String(roomTypeId)}
              onChange={(e) => setRoomTypeId(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">— non typée</option>
              {roomTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} — {t.labelFr}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          Capacité (personnes)
          <input
            type="number"
            min={1}
            max={20}
            value={maxOccupancy}
            onChange={(e) => setMaxOccupancy(Number(e.target.value))}
          />
        </label>

        {apartments ? (
          <>
            <p className="pms-sheet-section">Composition</p>
            <UnitScalars unit={unit} disabled={false} onChange={(patch) => setUnit((u) => ({ ...u, ...patch }))} />
            <SpaceEditor
              spaces={spaces}
              bedConfigs={bedConfigs}
              maxOccupancy={maxOccupancy}
              disabled={false}
              onChange={setSpaces}
            />
            {/* « Le nom du logement ne pourra plus être modifié » named neither
                field, and this form has two name-ish ones — so it was read as
                possibly meaning « Libellé catégorie » (operator, 2026-08-11).
                Say WHICH field, and quote what was typed into it. Nothing is
                claimed about the libellé: no RPC and no screen can edit a
                pms_room_type label today, so « modifiable ensuite » would be
                false and « définitif » would state a design decision that has
                not been taken. */}
            <p className="pms-hint pms-sheet-wide">
              La catégorie est créée avec le logement — chaque appartement est sa propre catégorie. Le champ
              « Nom du logement »{roomNumber.trim() !== '' && <> (« {roomNumber.trim()} »)</>} est définitif : c’est
              l’identité que suivront les réservations, et il ne pourra plus être modifié.
            </p>
          </>
        ) : (
          <>
            <label className="pms-sheet-wide">
              Lits
              <select
                value={bedConfig ?? ''}
                onChange={(e) => setBedConfig(e.target.value === '' ? null : e.target.value)}
              >
                <option value="">—</option>
                {bedConfigs.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.code} — {b.labelFr}
                  </option>
                ))}
              </select>
            </label>

            <p className="pms-hint pms-sheet-wide">
              Le reste (vue, chauffage, bain, WC, remarques) se renseigne ensuite dans la fiche. Le n° de chambre ne
              pourra plus être modifié.
            </p>
          </>
        )}
      </div>

      <footer className="pms-sheet-foot">
        <span className="pms-sheet-trace">
          {missing.length > 0 && `Pour créer : renseignez ${missing.join(', ')}.`}
        </span>
        <div className="pms-sheet-actions">
          <button type="button" className="auth-btn is-ghost" onClick={onClose} disabled={saving}>
            Annuler
          </button>
          <button
            type="button"
            className="auth-btn"
            disabled={!valid || saving}
            onClick={() =>
              onCreate(
                apartments
                  ? {
                      roomNumber: roomNumber.trim(),
                      floor: effectiveFloor as number,
                      // The category is CREATED with the unit, so no id is sent
                      // — the RPC refuses both at once, deliberately.
                      roomTypeId: null,
                      newType: {
                        code: typeCode.trim(),
                        labelFr: typeLabel.trim() || typeCode.trim(),
                        maxOccupancy,
                      },
                      configOptions: null,
                      doubleBedType: null,
                      maxOccupancy,
                      unit,
                      beds: spaces,
                    }
                  : {
                      roomNumber: roomNumber.trim(),
                      floor: effectiveFloor as number,
                      roomTypeId,
                      // A new hotel room states the arrangements it can take.
                      // `bedConfig` was one code carrying convertibility in its
                      // name; this is the set it always meant (0173).
                      configOptions: bedConfig === null ? [] : [bedConfig],
                      doubleBedType: null,
                      maxOccupancy,
                    },
              )
            }
          >
            {saving ? 'Création…' : 'Créer'}
          </button>
        </div>
      </footer>
    </aside>
  );
}
